import { defineStore } from 'pinia'

import { computed, ref } from 'vue'

import { ManagementApi } from '@/features/api/services/managementApi.js'
import { authApiEndpoints } from '@/features/api/services/authApi.js'
import { doParentLogout } from '@/features/api/unauthorizedResponseInterceptor.js'
import { clearTemporaryToken, getBearerToken, setBearerToken } from '@/features/auth/auth.utils.js'
import { useGetMyDetails } from '@/features/auth/useGetMyDetails.js'
import { AUTH_FLOW, reportAuthFlowError } from '@/features/auth/utils/reportAuthFlowError.js'
import { isInIframe } from '@/features/embed/isInIframe.js'
import {
  clearActiveClubId,
  finishClubImpersonation,
  getActiveClubId,
  getIsImpersonation,
} from '@/features/router/useActiveClub.js'

export const useAuthStore = defineStore('authStore', function () {
  const userTokenRef = ref(getBearerToken())
  const myDetails = ref(null)
  const myDetailsLoading = ref(null)
  const myDetailsError = ref(null)

  const avatarUrl = '/images/common/user-avatar.png'

  function getCurrentRouteUrl() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`
  }

  function buildAuthRoute(path, redirectUrl, extraParams = {}) {
    const params = new URLSearchParams()
    const activeClubId = getActiveClubId()

    if (activeClubId) {
      params.set('activeClubId', activeClubId)
    }

    params.set('redirectUrl', redirectUrl)
    for (const [key, value] of Object.entries(extraParams)) {
      if (value != null && value !== '') {
        params.set(key, value)
      }
    }

    return `${path}?${params.toString()}`
  }

  function isMyDetailsBadRequest(error) {
    const status = error?.response?.status
    const requestUrl = error?.config?.url || error?.response?.config?.url || ''

    return status === 400 && requestUrl.includes(authApiEndpoints.myDetails)
  }

  function redirectUnauthorized(error) {
    if (error?.response?.status !== 401) return false

    myDetails.value = null
    myDetailsError.value = error

    if (isInIframe()) {
      doParentLogout()
      return true
    }

    const currentPath = window.location.pathname
    if (currentPath.includes('/auth/logout') || currentPath.includes('/auth/login')) {
      return true
    }

    const redirectUrl = getCurrentRouteUrl()
    if (getBearerToken()) {
      window.location.replace(
        buildAuthRoute('/auth/logout', redirectUrl, { logoutFlow: 'sessionExpired' }),
      )
      return true
    }

    window.location.replace(buildAuthRoute('/auth/login', redirectUrl))
    return true
  }

  function redirectMyDetailsBadRequest(error) {
    if (!isMyDetailsBadRequest(error)) return false

    myDetails.value = null
    myDetailsError.value = error

    const currentUrl = getCurrentRouteUrl()

    if (window.location.pathname.includes('/profile-not-available')) {
      return true
    }

    window.location.replace(
      `/profile-not-available?${new URLSearchParams({
        reason: 'profile-unavailable',
        returnUrl: currentUrl,
      }).toString()}`,
    )

    return true
  }

  function setUserToken(token) {
    setBearerToken(token)
    userTokenRef.value = getBearerToken()
  }

  async function logout() {
    clearActiveClubId(null)
    setBearerToken(null)
    clearTemporaryToken()
    finishClubImpersonation(false)
    userTokenRef.value = null
  }

  const isLoggedIn = computed(() => !!userTokenRef.value)

  async function getImpersonatedClub(response) {
    const isImpersonation = getIsImpersonation(response)

    console.log('isImpersonation', isImpersonation)

    if (isImpersonation) {
      const impersonatedClub = await ManagementApi.tenantGetMyDetails()
      if (impersonatedClub?.value) {
        response.myTenants.push(impersonatedClub.value)
      }

      console.log(
        '[useAuthStore] Impersonated club found and added to myTenants:',
        impersonatedClub,
      )
    }

    return response
  }

  async function getMyDetails(setLoading = true) {
    try {
      if (setLoading) {
        myDetailsLoading.value = true
      }

      if (myDetails.value) {
        console.log('Returning cached myDetails')
        return myDetails.value
      }

      const getMyDetails = await useGetMyDetails()

      let response = await getMyDetails()

      // If response is null (network error), return null without processing
      if (response === null) {
        return null
      }

      response = await getImpersonatedClub(response)
      myDetails.value = response

      return response
    } catch (e) {
      reportAuthFlowError({
        flow: AUTH_FLOW.SESSION,
        step: 'get-my-details',
        error: e,
      })

      if (redirectMyDetailsBadRequest(e)) {
        return null
      }

      if (redirectUnauthorized(e)) {
        return null
      }

      const activeClubId = getActiveClubId()
      const redirectUrl = getCurrentRouteUrl()
      const errorUrl = new URL('/error', window.location.origin)
      errorUrl.searchParams.set('reason', 'auth-error')
      errorUrl.searchParams.set('redirectUrl', redirectUrl)

      if (activeClubId) {
        errorUrl.searchParams.set('activeClubId', activeClubId)
      }

      window.location.href = errorUrl.toString()
    } finally {
      myDetailsLoading.value = false
    }
  }

  async function getMyDetailsWithoutCache(setLoading = true) {
    try {
      if (setLoading) {
        myDetailsLoading.value = true
      }

      const getMyDetails = await useGetMyDetails()
      let response = await getMyDetails()
      console.log('getMyDetailsWithoutCache response', response)

      // If response is null (network error), return null without processing
      if (response === null) {
        return null
      }

      response = await getImpersonatedClub(response)
      myDetails.value = response

      return response
    } catch (e) {
      reportAuthFlowError({
        flow: AUTH_FLOW.SESSION,
        step: 'get-my-details-without-cache',
        error: e,
      })

      if (redirectMyDetailsBadRequest(e)) {
        return null
      }

      if (redirectUnauthorized(e)) {
        return null
      }

      myDetailsError.value = e
    } finally {
      myDetailsLoading.value = false
    }
  }

  const allClubs = computed(() => myDetails.value?.myTenants || [])

  const isImpersonating = computed(() => getIsImpersonation(myDetails.value))

  return {
    setUserToken,
    logout,
    userTokenRef,
    isLoggedIn,

    avatarUrl,

    getMyDetails,
    getMyDetailsWithoutCache,

    myDetailsLoading,
    myDetails,

    allClubs,
    myDetailsError,

    isImpersonating,
  }
})
